import React, { useState, useEffect, useRef } from "react";
import { getThemeColors, Theme } from "../theme/colors";
import { isDechraFn, isShellFn } from "../layout/chatSidebar";

type ChatInputProps = {
  theme: Theme;
  onMessageSubmit: (message: string) => Promise<void>;
  isDisabled: boolean;
  placeholderText?: string;
  passedInWidth?: string;
  username?: string;
  autoComplete?: Record<string, string>;
};

const ChatInput: React.FC<ChatInputProps> = ({
  theme,
  onMessageSubmit,
  isDisabled,
  placeholderText,
  passedInWidth = "100%",
  username = "default",
  autoComplete = {},
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [atPosition, setAtPosition] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if input contains incomplete @@ sequences
  const hasIncompleteAtSequence = (text: string): boolean => {
    // Only validate if autoComplete has content
    if (Object.keys(autoComplete).length === 0) return false;

    const atMatches = text.match(/ @[\w().,/'&%+/-]*/g);
    if (!atMatches) return false;

    return atMatches.some((match) => {
      const key = match.substring(2); // Remove space and @
      return key.length > 0 && !(key in autoComplete);
    });
  };

  // Get invalid @@ sequences for error message
  const getInvalidSequences = (text: string): string[] => {
    // Only get invalid sequences if autoComplete has content
    if (Object.keys(autoComplete).length === 0) return [];

    const atMatches = text.match(/ @[\w().,/'&%+/-]*/g);
    if (!atMatches) return [];

    return atMatches
      .filter((match) => {
        const key = match.substring(2); // Remove space and @
        return key.length > 0 && !(key in autoComplete);
      })
      .map((match) => match.substring(2)); // Return just the key without space and @
  };

  const handleSend = () => {
    if (isDisabled) return;
    if (hasIncompleteAtSequence(inputValue)) return; // Prevent submission with incomplete @@ sequences

    const message = inputValue;
    if (message.trim() === "") return;
    if (onMessageSubmit) {
      onMessageSubmit(message);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    setInputValue(value);

    // Find the last @@ before cursor position
    let lastAtIndex = -1;
    for (let i = cursorPosition - 2; i >= 0; i--) {
      if (value.substring(i, i + 2) === " @") {
        lastAtIndex = i;
        break;
      }
      if (value[i] === " ") {
        break; // Stop at space
      }
    }

    if (lastAtIndex !== -1) {
      const searchTerm = value
        .substring(lastAtIndex + 2, cursorPosition)
        .toLowerCase();
      const filtered = Object.entries(autoComplete)
        .filter(([key]) => key.toLowerCase().startsWith(searchTerm))
        .map(([key, value]) => ({ key, value }));

      // Only show dropdown if autoComplete has content and there are matches
      if (
        filtered.length > 0 &&
        searchTerm.length >= 0 &&
        Object.keys(autoComplete).length > 0
      ) {
        setFilteredOptions(filtered);
        setShowDropdown(true);
        setSelectedIndex(0);
        setAtPosition(lastAtIndex);
      } else {
        setShowDropdown(false);
      }
    } else {
      setShowDropdown(false);
    }
  };

  const selectOption = (option: { key: string; value: string }) => {
    const beforeAt = inputValue.substring(0, atPosition);
    const afterCursor = inputValue.substring(
      inputRef.current?.selectionStart || inputValue.length
    );
    const newValue = beforeAt + " @" + option.key + afterCursor;

    setInputValue(newValue);
    setShowDropdown(false);

    // Focus back to input
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPosition = beforeAt.length + option.key.length + 2;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredOptions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(
          (prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredOptions[selectedIndex]) {
          selectOption(filteredOptions[selectedIndex]);
        }
        return;
      } else if (e.key === "Escape") {
        setShowDropdown(false);
      }
    }

    if (e.key === "Enter" && !isDisabled && !showDropdown) {
      handleSend();
    }
  };

  useEffect(() => {
    if (!isDisabled) {
      setInputValue("");
    }
  }, [isDisabled]);

  const currentTheme = getThemeColors(username, theme);

  return (
    <>
      <div
        style={{
          position: "relative",
          width: passedInWidth,
          marginBottom: "15px",
        }}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder={
            isDisabled
              ? "Thinking"
              : placeholderText
                ? placeholderText
                : "Type a question..."
          }
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          style={{
            width: "100%",
            padding: "10px 30px 10px 10px",
            boxSizing: "border-box",
            border: `1px solid ${hasIncompleteAtSequence(inputValue)
              ? currentTheme.inputErrorBorder
              : currentTheme.border
              }`,
            paddingRight: "25px",
            backgroundColor: isDisabled
              ? currentTheme.disabledBg
              : currentTheme.background,
            borderRadius: "20px",
            outline: "none",
            color: isDisabled ? currentTheme.disabledText : currentTheme.text,
            cursor: isDisabled ? "not-allowed" : "text",
          }}
        />

        {/* Autocomplete Dropdown */}
        {showDropdown && filteredOptions.length > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: "100%",
              left: "0",
              right: "0",
              marginBottom: "5px",
              backgroundColor: currentTheme.background,
              border: `1px solid ${currentTheme.border}`,
              borderRadius: "8px",
              maxHeight: "200px",
              overflowY: "auto",
              zIndex: 1000,
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            {filteredOptions.map((option, index) => (
              <div
                key={option.key}
                onClick={() => selectOption(option)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor:
                    index === selectedIndex
                      ? currentTheme.border
                      : "transparent",
                  borderBottom:
                    index < filteredOptions.length - 1
                      ? `1px solid ${currentTheme.border}`
                      : "none",
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    color: currentTheme.text,
                    fontSize: "14px",
                  }}
                >
                  @{option.key}
                </div>
                <div
                  style={{
                    color: currentTheme.text,
                    opacity: 0.7,
                    fontSize: "12px",
                    marginTop: "2px",
                  }}
                >
                  {option.value}
                </div>
              </div>
            ))}
          </div>
        )}

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          onClick={
            isDisabled || hasIncompleteAtSequence(inputValue)
              ? undefined
              : handleSend
          }
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            fill:
              isDisabled || hasIncompleteAtSequence(inputValue)
                ? currentTheme.disabledSvgFill
                : currentTheme.svgFill,
            cursor:
              isDisabled || hasIncompleteAtSequence(inputValue)
                ? "not-allowed"
                : "pointer",
            opacity: hasIncompleteAtSequence(inputValue) ? 0.5 : 1,
          }}
        >
          <path d="M2.56322 7.88589C2.02122 8.11339 1.6848 8.51838 1.67972 9.09172C1.67622 9.49255 1.94672 10.0309 2.48872 10.2509C2.71197 10.3417 5.84655 10.7634 5.84655 10.7634C5.84655 10.7634 6.67622 13.3859 6.97522 14.3076C7.0618 14.5742 7.11114 14.7051 7.30114 14.8792C7.62347 15.1742 8.16839 15.0817 8.40405 14.8451C9.02755 14.2201 10.0131 13.2559 10.0131 13.2559L10.4278 13.5934C10.4278 13.5934 12.2696 15.0626 13.2763 15.7542C13.8691 16.1617 14.2805 16.5867 14.9476 16.5892C15.2875 16.5909 15.8326 16.4217 16.1929 16.0092C16.431 15.7367 16.5837 15.3009 16.6428 14.9101C16.7771 14.0225 18.3531 4.42589 18.3464 4.09006C18.3357 3.55423 17.885 3.25255 17.5103 3.25589C17.275 3.25839 17.0811 3.32673 16.6496 3.4584C13.3117 4.47756 2.7843 7.79339 2.56322 7.88589ZM15.0131 5.75589C15.0131 5.75589 10.6147 9.58423 8.85714 11.3459C8.29405 11.9101 8.2543 12.8784 8.2543 12.8784L7.34581 9.97172L15.0131 5.75589Z" />
        </svg>

        {/* Error message for invalid sequences */}
        {hasIncompleteAtSequence(inputValue) &&
          Object.keys(autoComplete).length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                right: "0",
                marginTop: "5px",
                padding: "8px 10px",
                backgroundColor: currentTheme.background,
                border: `1px solid ${currentTheme.inputErrorBorder}`,
                borderRadius: "8px",
                fontSize: "12px",
                color: currentTheme.inputErrorBorder,
                zIndex: 999,
              }}
            >
              Invalid shorthand
              {getInvalidSequences(inputValue).length > 1 ? "s" : ""}:{" "}
              {getInvalidSequences(inputValue)
                .map((seq) => `"${seq}"`)
                .join(", ")}
            </div>
          )}
      </div>
      {(isDechraFn(username) || isShellFn(username)) && (
        <div
          style={{
            width: passedInWidth,
            textAlign: "right",
            marginTop: "5px",
          }}
        >
          <p
            style={{
              margin: "-9px",
              marginRight: "11px",
              fontSize: "12px",
              color: currentTheme.text,
              opacity: 0.7,
            }}
          >
            Powered by Straive
          </p>
        </div>
      )}
    </>
  );
};

export default ChatInput;
